import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface SignaturePadProps {
  label?: string;
  /** Marks the field required in the label. */
  required?: boolean;
  /**
   * Bare base64 PNG, no `data:` prefix — the same shape `fileToBase64` returns,
   * which is what the APIs in this app expect. Clearing this to an empty value
   * resets the pad, so a parent can wipe it when a modal reopens.
   */
  value?: string;
  /** Emits bare base64 on each finished stroke, or null once cleared. */
  onChange: (value: string | null) => void;
  disabled?: boolean;
  error?: string;
  /** Drawing area height in CSS pixels. */
  height?: number;
  helperText?: string;
  className?: string;
}

interface Point {
  x: number;
  y: number;
}

const STROKE_COLOR = '#111827';
const STROKE_WIDTH = 2;

/**
 * Draw-to-sign canvas, dependency-free.
 *
 * Strokes are kept as points rather than living only in the bitmap, so the pad
 * can be redrawn after a resize — resizing a canvas resets its backing store
 * and would otherwise erase a signature someone had already drawn. The backing
 * store is scaled by devicePixelRatio so lines are not blurry on retina.
 *
 * Pointer events cover mouse, touch and pen through one API, so a signature can
 * be drawn on a laptop trackpad or a touchscreen without separate handlers.
 */
export const SignaturePad: React.FC<SignaturePadProps> = ({
  label,
  required = false,
  value,
  onChange,
  disabled = false,
  error,
  height = 160,
  helperText,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Point[][]>([]);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  /** Repaints every stored stroke onto a cleared white ground. */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    // Filled in device pixels with the transform reset, so the whole backing
    // store is covered whatever scale is currently applied.
    // White rather than transparent: the signature ends up composited into the
    // BAST document, where a transparent PNG can render as a black block.
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.restore();

    context.strokeStyle = STROKE_COLOR;
    context.lineWidth = STROKE_WIDTH;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    strokesRef.current.forEach((stroke) => {
      if (!stroke.length) return;
      context.beginPath();
      // A single tap still needs to leave a mark, so a one-point stroke is
      // drawn as a dot rather than an empty path.
      if (stroke.length === 1) {
        context.arc(stroke[0].x, stroke[0].y, STROKE_WIDTH / 2, 0, Math.PI * 2);
        context.fillStyle = STROKE_COLOR;
        context.fill();
        return;
      }
      context.moveTo(stroke[0].x, stroke[0].y);
      stroke.slice(1).forEach((point) => context.lineTo(point.x, point.y));
      context.stroke();
    });
  }, []);

  /**
   * Matches the backing store to the element's CSS size, then repaints.
   *
   * A no-op when the size already agrees, which makes it safe to call as often
   * as we like — including from a ResizeObserver, where changing the canvas
   * would otherwise risk a notification loop.
   */
  const syncSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;

    const ratio = window.devicePixelRatio || 1;
    const width = Math.round(rect.width * ratio);
    const height = Math.round(rect.height * ratio);
    if (canvas.width === width && canvas.height === height) return;

    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.setTransform(ratio, 0, 0, ratio, 0, 0);
    redraw();
  }, [redraw]);

  useEffect(() => {
    syncSize();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWindowResize = () => syncSize();
    window.addEventListener('resize', handleWindowResize);

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => syncSize())
        : null;
    observer?.observe(canvas);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      observer?.disconnect();
    };
  }, [syncSize]);

  const clear = useCallback(
    (emit: boolean) => {
      strokesRef.current = [];
      setHasInk(false);
      redraw();
      if (emit) onChange(null);
    },
    [onChange, redraw]
  );

  // Lets a parent reset the pad by clearing `value` — e.g. when a modal
  // reopens for a different rental.
  useEffect(() => {
    if (!value && hasInk) clear(false);
  }, [value, hasInk, clear]);

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;

    // Re-check the size before the first point rather than trusting that a
    // resize notification arrived. Pointer coordinates come from the element's
    // CSS box, so a backing store still sized for an older width would place
    // the whole stroke wrong. Cheap, exact, and it needs no observer to fire.
    syncSize();

    // Keeps the stroke coming to this canvas even if the pointer leaves it,
    // so a signature that overshoots the edge does not end mid-stroke.
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    strokesRef.current.push([pointAt(event)]);
    redraw();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    strokesRef.current[strokesRef.current.length - 1]?.push(pointAt(event));
    redraw();
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setHasInk(true);
    const dataUrl = canvasRef.current?.toDataURL('image/png');
    // Bare base64, matching fileToBase64 and what the APIs here accept.
    onChange(dataUrl ? dataUrl.split(',')[1] : null);
  };

  return (
    <div className={className}>
      {label && (
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-[14px] text-gray-900">
            {label} {required && <span className="text-primary">*</span>}
          </label>
          <button
            type="button"
            onClick={() => clear(true)}
            disabled={disabled || !hasInk}
            className="text-[13px] text-gray-500 hover:text-gray-700 disabled:text-gray-300"
          >
            Hapus
          </button>
        </div>
      )}

      <div
        className={`relative rounded-lg border bg-white ${
          error ? 'border-red-500' : 'border-gray-200'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <canvas
          ref={canvasRef}
          style={{ height, touchAction: 'none' }}
          className="w-full rounded-lg block cursor-crosshair"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />

        {!hasInk && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[13px] text-gray-400">
            Tanda tangan di sini
          </span>
        )}
      </div>

      {error ? (
        <p className="mt-1 text-[12px] text-red-500">{error}</p>
      ) : (
        helperText && (
          <p className="mt-1 text-[12px] text-gray-500">{helperText}</p>
        )
      )}
    </div>
  );
};

export default SignaturePad;
